import React from "react";
import { createPortal } from "react-dom";
import "./PatientAnketaPrint.css";

/**
 * Печатная форма анкеты пациента.
 *
 * Отдельный компонент, потому что экранный вид (просмотр/редактирование)
 * и бумажный бланк требуют разной вёрстки:
 *   • на экране — «Подпись: значение на линии», без рамок и полей;
 *   • на бумаге — аккуратный бланк с полями, рамками таблиц и отступами A4.
 *
 * ПОРТАЛ В <body> — принципиально:
 *   • все документы медкарты печатаются глобальными правилами
 *     `body.print-only-profile ...` из patients.css, а `@page` там
 *     перебивает последний по бандлу `PatientPlanPrintOtherFormat.css`
 *     (`margin: 0 0 8mm 0 !important`) — из-за него у анкеты пропадали
 *     верхние поля и отступы между листами;
 *   • поэтому бланк рендерится прямо в <body>, а корень приложения на
 *     время печати скрывается (`body.anketa-print-mode #root`), и поля
 *     листа задаются отдельным <style>, который вставляет handlePrint
 *     (последний тег в <head> побеждает любые чужие @page).
 */

const EMPTY = "—";

const pick = (obj, lang) => {
    if (obj == null) return "";
    if (typeof obj === "string") return obj;
    return obj[lang] || obj.ru || obj.en || "";
};

const asText = (value) => {
    if (value === 0) return "0";
    if (value == null) return "";
    return String(value).trim();
};

// Подписи выбранных вариантов (чекбоксы/радио) через запятую.
const selectedOptionsText = (options, selected, lang) => {
    const keys = Array.isArray(selected) ? selected : selected ? [selected] : [];
    return (options || [])
        .filter((opt) => keys.includes(opt.key))
        .map((opt) => pick(opt.label, lang))
        .join(", ");
};

/** Значение в виде обычного текста (для строк «подпись — значение»). */
function plainValue(field, value, lang) {
    switch (field.kind) {
        case "checks":
        case "radio":
            return selectedOptionsText(field.options, value, lang);
        case "scale":
            return asText(value);
        default:
            return asText(value);
    }
}

function PrintRow({ label, value, unit }) {
    const text = asText(value);
    return (
        <div className="anketa-print__row">
            <span className="anketa-print__label">
                {label}:
            </span>
            <span className="anketa-print__value">
                {text === "" ? EMPTY : text}
                {unit && text !== "" ? (
                    <span className="anketa-print__unit"> {unit}</span>
                ) : null}
            </span>
        </div>
    );
}

function PrintTable({ field, value, lang }) {
    const columns = field.columns || [];
    const rows = Array.isArray(value) ? value : [];
    const filled = rows.filter((row) =>
        columns.some((_col, i) => asText(row && row[i]) !== ""),
    );
    const printable = filled.length ? filled : [{}];

    return (
        <div className="anketa-print__table-wrap">
            <table className="anketa-print__table">
                <thead>
                    <tr>
                        {columns.map((col, i) => (
                            <th key={i}>{pick(col, lang)}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {printable.map((row, r) => (
                        <tr key={r}>
                            {columns.map((_col, i) => (
                                <td key={i}>
                                    {asText(row && row[i]) || EMPTY}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function PrintPairs({ field, value, lang }) {
    const obj =
        value && typeof value === "object" && !Array.isArray(value) ? value : {};
    return (
        <div className="anketa-print__pairs">
            {(field.items || []).map((item) => (
                <PrintRow
                    key={item.key}
                    label={pick(item.label, lang)}
                    unit={item.unit ? pick(item.unit, lang) : ""}
                    value={obj[item.key]}
                />
            ))}
        </div>
    );
}

function PrintField({ field, value, lang }) {
    switch (field.kind) {
        case "sub":
            return (
                <div className="anketa-print__sub">
                    {pick(field.title, lang)}
                </div>
            );

        case "note":
            return (
                <div className="anketa-print__note">
                    {pick(field.text, lang)}
                </div>
            );

        case "table":
            return (
                <div className="anketa-print__field anketa-print__field--stack">
                    <span className="anketa-print__label anketa-print__label--block">
                        {pick(field.label, lang)}:
                    </span>
                    <PrintTable field={field} lang={lang} value={value} />
                </div>
            );

        case "pairs":
            return (
                <div className="anketa-print__field anketa-print__field--stack">
                    <span className="anketa-print__label anketa-print__label--block">
                        {pick(field.label, lang)}:
                    </span>
                    <PrintPairs field={field} lang={lang} value={value} />
                </div>
            );

        case "textarea": {
            const text = asText(value);
            return (
                <div className="anketa-print__field anketa-print__field--stack">
                    <span className="anketa-print__label anketa-print__label--block">
                        {pick(field.label, lang)}:
                    </span>
                    <span className="anketa-print__value anketa-print__value--multiline">
                        {text === "" ? EMPTY : text}
                    </span>
                </div>
            );
        }

        default:
            if (!field.label) return null;
            return (
                <PrintRow
                    label={pick(field.label, lang)}
                    value={plainValue(field, value, lang)}
                />
            );
    }
}

function AnketaPrintSheet({
    sections = [],
    values = {},
    lang = "ru",
    patientName = "",
}) {
    const printedAt = new Date().toLocaleDateString(
        lang === "en" ? "en-GB" : "ru-RU",
        { day: "2-digit", month: "2-digit", year: "numeric" },
    );
    const name = asText(patientName) || asText(values.patient_name);

    return (
        <div className="anketa-print">
            <header className="anketa-print__head">
                <div className="anketa-print__title">
                    {lang === "en"
                        ? "PATIENT QUESTIONNAIRE"
                        : "АНКЕТА ПАЦИЕНТА"}
                </div>
                <div className="anketa-print__meta">
                    {name ? (
                        <span className="anketa-print__meta-item">
                            {lang === "en" ? "Patient: " : "Пациент: "}
                            <strong>{name}</strong>
                        </span>
                    ) : null}
                    <span className="anketa-print__meta-item">
                        {lang === "en" ? "Date: " : "Дата: "}
                        <strong>{printedAt}</strong>
                    </span>
                </div>
            </header>

            {sections.map((section) => (
                <section className="anketa-print__section" key={section.id}>
                    <h2 className="anketa-print__section-title">
                        <span className="anketa-print__section-num">
                            {section.num}.
                        </span>{" "}
                        {pick(section.title, lang)}
                    </h2>
                    {section.note ? (
                        <p className="anketa-print__section-note">
                            {pick(section.note, lang)}
                        </p>
                    ) : null}
                    <div className="anketa-print__body">
                        {(section.fields || []).map((field) => (
                            <PrintField
                                field={field}
                                key={field.id}
                                lang={lang}
                                value={values[field.id]}
                            />
                        ))}
                    </div>
                </section>
            ))}
        </div>
    );
}

/**
 * Публичный компонент: рендерит бланк порталом в <body>.
 * На экране он скрыт (`.anketa-print { display: none }`), а при включённом
 * режиме печати (`body.anketa-print-mode`) — показывается, причём корень
 * приложения в это время скрыт (см. PatientAnketaPrint.css).
 */
export default function PatientAnketaPrint(props) {
    if (typeof document === "undefined" || !document.body) return null;
    return createPortal(<AnketaPrintSheet {...props} />, document.body);
}

